import React, { useEffect, useState } from "react";
import Common from "../../../Components/Layouts/Common.js";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../../Hooks/useApi.js";
import Button from "../../../Components/UI/Buttons/Button.js";
import { handleApiError } from "../../../Helpers/ErrorHandler.js";

function ViewSurveyQuestionnaire() {
  const location = useLocation();
  const { id, viewType } = location?.state || {};

  const [questionnaireMeta] = useState(id ? { id } : null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [questionnaireData, setQuestionnaireData] = useState();

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { request } = useApi();

  const fetchQuestionnaireData = async () => {
    setLoading(true);
    try {
      const response = await request(
        "GET",
        `/v1/survey/questionnaires/questions/${questionnaireMeta?.id}`,
        {},
        "survey_service"
      );

      setQuestionnaireData({
        ...response,
        sections:
          response.questions && response.questions.length
            ? [
                {
                  sectionName: "Without Section",
                  order: -1,
                  questions: response.questions,
                },
                ...response.sections,
              ]
            : [...response.sections],
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (questionnaireMeta?.id) {
      fetchQuestionnaireData();
    }
  }, [questionnaireMeta?.id]);

  // Roman numeral converter
  const toRoman = (num) => {
    const roman = [
      "I",
      "II",
      "III",
      "IV",
      "V",
      "VI",
      "VII",
      "VIII",
      "IX",
      "X",
      "XI",
      "XII",
      "XIII",
      "XIV",
      "XV",
      "XVI",
      "XVII",
      "XVIII",
      "XIX",
      "XX",
    ];
    return roman[num] || num + 1; // Fallback for large numbers
  };
  const markCompleteHandler = async () => {
    try {
      const response = await request(
        "POST",
        `/v1/survey/questionnaires/finalize`,
        {
          data: {
            questionnaireId: id,
            status: "COMPLETED",
          },
        },
        "survey_service"
      );

      if (response) {
        navigate("/survey-questionnaire");
      }
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };

  const makeChangesHandler = () => {
    try {
      setTimeout(() => {
        navigate("/create-survey-questionnaire", {
          state: {
            name: questionnaireData?.name,
            id: id,
          },
        });
      });
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };
  const fromTemplateHandler = () => {
    try {
      setTimeout(() => {
        navigate("/create-survey-questionnaire", {
          state: {
            id: id,
            name: questionnaireData?.name,
            isTemplate: true,
          },
        });
      });
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };

  return (
    <Common headline={`View Survey Questionnaire `}>
      <div className="container mx-auto p-4">
        <div className=" bg-white shadow-lg rounded-lg overflow-hidden ">
          <div className="p-6">
            {loading ? (
              <p className="text-center text-gray-500">Loading...</p>
            ) : (
              <>
                {/* Questionnaire Header */}
                <div className="flex mb-4 ">
                  <h4 className=" font-bold text-gray-800 pr-2">
                    Questionnaire Name:
                  </h4>
                  <h5>{questionnaireData?.name}</h5>
                </div>
                <div className="">
                  {/* Tabs for Sections */}
                  <div className="flex overflow-auto border-b mb-4">
                    {questionnaireData?.sections
                      .sort((a, b) => a.sectionOrder - b.sectionOrder)
                      .map((section, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveSectionIndex(index)}
                          className={`whitespace-nowrap px-4 py-2 font-medium ${
                            activeSectionIndex === index
                              ? "text-blue-600 border-b-2 border-blue-600"
                              : "text-gray-600 hover:text-blue-600"
                          }`}
                        >
                          {section.sectionName}
                        </button>
                      ))}
                  </div>
                </div>
                {/* Scrollable Questions Content */}
                <div
                  className="h-[45vh] overflow-auto p-4 bg-gray-50 rounded-lg"
                  style={{ maxHeight: "60vh" }}
                >
                  {questionnaireData?.sections[activeSectionIndex]?.questions
                    .sort((a, b) => a.order - b.order)
                    .map((question, qIndex) => (
                      <div
                        key={qIndex}
                        className="mb-4 p-4 bg-white shadow rounded-md"
                      >
                        {/* Question Number and Content */}
                        <h4 className="font-semibold text-lg text-gray-800 mb-2">
                          {qIndex + 1}. {question.name}
                        </h4>

                        {/* Question Type */}
                        <p className="text-sm text-gray-500 mb-2">
                          Type: {question.type}
                        </p>

                        {/* Options with Roman Numerals */}
                        {question.questionOptions && (
                          <ul className="list-none pl-4">
                            {question.questionOptions
                              .sort((a, b) => a.order - b.order)
                              .map((option, oIndex) => (
                                <li
                                  key={oIndex}
                                  className="text-gray-700 flex items-center mb-1"
                                >
                                  <span className="font-bold mr-2">
                                    {toRoman(oIndex)}.
                                  </span>
                                  {option?.name}
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
        {viewType === "write" ? (
          <div className="confirmationButtonGroup text-center pt-2 space-x-3">
            <Button
              icon="fa-solid fa-pen-to-square"
              type="iconButton"
              label={"Make Additional Changes"}
              backgroundColor={"transparent"}
              textColor="#1976d2"
              handler={makeChangesHandler}
            />
            <Button
              icon={"fas fa-check"}
              type="iconButton"
              label={"Mark Complete"}
              textColor="#1976d2"
              backgroundColor={"transparent"}
              handler={markCompleteHandler}
            />
          </div>
        ) : viewType === "modify" ? (
          <div className="confirmationButtonGroup text-center pt-2 space-x-3">
            <Button
              icon="fa-solid fa-pen-to-square"
              type="iconButton"
              label={"Create From This Template"}
              backgroundColor={"transparent"}
              textColor="#1976d2"
              handler={fromTemplateHandler}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
    </Common>
  );
}

export default ViewSurveyQuestionnaire;
